// blockBoard — the Pages-tab drag-and-drop block lists (Available/Active
// columns + rows + drag overlay), extracted verbatim from WebshopConfigScreen.
// Module scope ON PURPOSE: defined inline they got a fresh identity every
// render, so React unmounted/remounted every row (and re-registered it with
// the DnD context) on each keystroke while the Pages tab was open. The screen
// passes its theme values + handlers in one `board` prop.
import { Box, IconButton, Paper, Typography } from '@mui/material';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import Colors from '../../../library/Colors';

const BlockRowContent = ({ item, column, board }) => {
    const { mutedText, textColor, blockLabel, openEditContainerModal, deleteContainer, setConfigBlock } = board;
    return (
    <>
        <DragIndicatorIcon sx={{ color: mutedText, mr: 1.5, fontSize: 20 }} />
        <Typography sx={{ flexGrow: 1, color: textColor, fontSize: '1rem' }}>
            {blockLabel(item)}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
            {item.isContainer && (
                <IconButton
                    size="small"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => { e.stopPropagation(); openEditContainerModal(item); }}
                    sx={{ color: mutedText }}
                    title="Configure container"
                >
                    <SettingsIcon fontSize="small" />
                </IconButton>
            )}
            {item.isContainer && (
                <IconButton
                    size="small"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => { e.stopPropagation(); deleteContainer(item.id); }}
                    sx={{ color: Colors.def_red }}
                    title="Delete container"
                >
                    <DeleteIcon fontSize="small" />
                </IconButton>
            )}
            {column === 'active' && item.configurable && !item.isContainer && (
                <IconButton
                    size="small"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => { e.stopPropagation(); setConfigBlock(item); }}
                    sx={{ color: mutedText }}
                >
                    <SettingsIcon fontSize="small" />
                </IconButton>
            )}
        </Box>
    </>
);
};

const BlockRow = ({ item, column, board }) => {
    const { cardBg, borderColor } = board;
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: item.id, data: { column } });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0 : 1,
    };

    return (
        <Paper
            ref={setNodeRef}
            variant="outlined"
            style={style}
            {...attributes}
            {...listeners}
            sx={{
                display: 'flex',
                alignItems: 'center',
                px: 2,
                py: 1.5,
                mb: 1.5,
                cursor: 'grab',
                backgroundColor: cardBg,
                borderColor: borderColor,
                borderRadius: 2,
                userSelect: 'none',
                '&:hover': {
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                }
            }}
        >
            <BlockRowContent item={item} column={column} board={board} />
        </Paper>
    );
};

const BlockRowOverlay = ({ item, column, board }) => {
    const { cardBg, borderColor } = board;
    return (
    <Paper
        variant="outlined"
        sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 1.5,
            cursor: 'grabbing',
            backgroundColor: cardBg,
            borderColor: borderColor,
            borderRadius: 2,
            userSelect: 'none',
            boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
        }}
    >
        <BlockRowContent item={item} column={column} board={board} />
    </Paper>
);
};

const DroppableColumn = ({ column, items, label, board }) => {
    const { textColor, mutedText, borderColor, panelBg, isDark, openNewContainerModal } = board;
    const { setNodeRef, isOver } = useDroppable({ id: column });
    const isEmpty = items.length === 0;
    // Highlight the whole column whenever a block is dragged over it (not
    // only when empty), so the drop target reads clearly during a drag.
    const highlight = isOver;

    return (
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                <Typography variant="h6" sx={{ color: textColor, fontWeight: 500 }}>
                    {label}
                </Typography>
                {column === 'available' && (
                    <IconButton
                        size="small"
                        onClick={openNewContainerModal}
                        sx={{ color: Colors.sft_blue }}
                        title="Add product container"
                    >
                        <AddIcon fontSize="small" />
                    </IconButton>
                )}
            </Box>
            <Paper
                ref={setNodeRef}
                variant="outlined"
                sx={{
                    backgroundColor: highlight
                        ? (isDark ? '#2a3340' : '#eef4ff')
                        : panelBg,
                    borderColor: highlight ? Colors.sft_blue : borderColor,
                    borderRadius: 2,
                    p: 2,
                    minHeight: 420,
                    borderStyle: highlight || isEmpty ? 'dashed' : 'solid',
                    borderWidth: highlight ? 2 : 1,
                    transition: 'background-color 200ms, border-color 200ms'
                }}
            >
                <SortableContext items={items.map(i => i.id)} strategy={verticalListSortingStrategy}>
                    {items.map(item => (
                        <BlockRow key={item.id} item={item} column={column} board={board} />
                    ))}
                </SortableContext>
                {isEmpty && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, color: mutedText, fontStyle: 'italic' }}>
                        Drop blocks here
                    </Box>
                )}
            </Paper>
        </Box>
    );
};

export { DroppableColumn, BlockRowOverlay };
